import { LeftOutlined, SettingOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Layout, message, Modal, Spin, Tabs, notification } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { useMsal } from "@azure/msal-react";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import { useNavigationBlocker } from "../hooks/useNavigationBlocker";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { useVisibilityTracking } from "../hooks/useVisibilityTracking";
import { useWebSocketStatus } from "../hooks/useWebSocketStatus";
import NavigationConfirmModal from "../components/common/NavigationConfirmModal";
import ErrorBoundary from "../components/common/ErrorBoundary";
import AINotesView from "../components/features/sessions/AINotesView";
import ConnectionStatus from "../components/features/sessions/ConnectionStatus";
import FeedbackModal from "../components/features/sessions/FeedbackModal";
import ManualNotes from "../components/features/sessions/ManualNotes";
import PastSummaryView from "../components/features/sessions/PastSummaryView";
import PatientDetailsHeader from "../components/features/sessions/PatientDetailsHeader";
import SearchModal from "../components/features/sessions/SearchModal";
import SessionRightPanel, {
  decodeBase64,
} from "../components/features/sessions/SessionRightPanel";
import SessionSidebar from "../components/features/sessions/SessionSidebar";
import SettingsModal from "../components/features/sessions/SettingsModal";
import TranscriptRecording from "../components/features/sessions/TranscriptRecording";
import TranscriptView from "../components/features/sessions/TranscriptView";
import TranscriptUploadModal from "../components/features/sessions/TranscriptUploadModal";
import { Header } from "../components/layout/header";
import {
  getPastSummary,
  getPatientDetails,
  getRecordingTranscript,
  getSessions,
  getTranscript,
  // submitFeedback,  // COMMENTED OUT (RCA UX5): Was a mock function. Real feedback is handled in AINotesView.js
} from "../services/api";

const { Content } = Layout;
const { TabPane } = Tabs;

function formatReadableDate(dateStr) {
  const isoString = dateStr?.replace?.(" ", "T");
  const date = new Date(isoString);
  if (isNaN(date)) return "Invalid Date";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleString("en-US", options);
}
function extractTimeWithoutSeconds(dateStr) {
  const isoString = dateStr?.replace?.(" ", "T");
  const date = new Date(isoString);
  if (isNaN(date)) return "Invalid Time";
  const options = { hour: "numeric", minute: "2-digit", hour12: true };
  return date.toLocaleTimeString("en-US", options);
}

const SessionDetails = ({
  selectedDoctorId,
  setSelectedDoctorId,
  selectedDate,
  setSelectedDate,
  options = {}, // fixed: was `options = { options }`
}) => {
  const { patient_id: id, id: session_id } = useParams();
  const { instance } = useMsal();
  const location = useLocation();

  const [patientDetails, setPatientDetails] = useState(null);
  const [parentRecording, setParentRecording] = useState(
    location?.state?.is_recording
  );
  const [dateRange, setDateRange] = useState([null, null]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(session_id);
  const [selectedSummarySessionId, setSelectedSummarySessionId] = useState("");
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);

  const [activeTab, setActiveTab] = useState("2");
  const [notes, setNotes] = useState("");
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [settingsVisible, setSettingsVisible] = useState(false);

  const [timer, setTimer] = useState("00:00:00");
  const [fetchedTranscript, setFetchedTranscript] = useState("");
  const [fieldMappings, setFieldMappings] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [isNoteSigned, setIsNoteSigned] = useState(false);
  const [transcriptReceived, setTranscriptReceived] = useState(false);
  const [meetingStatus, setMeetingStatus] = useState("ended");
  const [template, setTemplate] = useState("Therapist Progress Note 2022");
  const [noteJSON, setNoteJSON] = useState({});
  const [recordingTranscript, setRecordingTranscript] = useState([]);
  const [livePreview, setLivePreview] = useState(new Map());

  const [hasConsent, setHasConsent] = useState(false);
  const handleConsentGiven = () => setHasConsent(true);
  const handleConsentClear = () => setHasConsent(false);

  const [loadingCore, setLoadingCore] = useState(true);
  const [loadingPastSummary, setLoadingPastSummary] = useState(false);
  const [pastSummary, setPastSummary] = useState(null);
  const [pastSummaryStatus, setPastSummaryStatus] = useState(null);
  const [pastSummaryErrorMessage, setPastSummaryErrorMessage] = useState(null);
  const [pastSummarySessionsProcessed, setPastSummarySessionsProcessed] =
    useState(null);

  const [noteCommentSessionId, setNoteCommentSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [fetchingNote, setFetchingNote] = useState(false);
  const [pollingNote, setPollingNote] = useState(false);
  const [recordingParent, setRecordingParent] = useState(false);
  const [noteSubmitted, setNoteSubmitted] = useState(false);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [postSummaryVisible, setPostSummaryVisible] = useState(false);
  const [currentSessionType, setCurrentSessionType] = useState(null);
  const [noteLocked, setNoteLocked] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false); // demo-only transcript upload
  const initialSummaryFetchedRef = useRef(false);
  const [pastSummaryDirty, setPastSummaryDirty] = useState(true);

  // EHR submission status
  const [isEhrSubmitted, setIsEhrSubmitted] = useState(false);

  // Network status detection (browser online/offline)
  const { isOnline } = useNetworkStatus();

  // WebSocket status management (WebSocket connection state)
  const { wsState, setWsState } = useWebSocketStatus();

  // Reconnection attempt tracking
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Track if WebSocket connection permanently failed during recording
  const [wsConnectionFailed, setWsConnectionFailed] = useState(false);

  // ---------------------------------------------------------------------------
  // Valid Appointment Flag — blocks recording, AI notes, and EHR submission
  // Primary: from location.state (Dashboard navigation)
  // Secondary: detected from UNSUPPORTED_SESSION_TYPE error on API calls
  // ---------------------------------------------------------------------------
  const [isValidAppointment, setIsValidAppointment] = useState(
    location.state?.valid_appt_flag !== undefined
      ? location.state.valid_appt_flag !== false
      : true // optimistic default; secondary detection will correct if needed
  );

  // ---------------------------------------------------------------------------
  // Idle Session Timeout Detection (Frontend Logging Gap Analysis — Proposal #3)
  // Emits session.timeout telemetry event when the user is idle for 30 minutes.
  // ---------------------------------------------------------------------------
  useIdleTimeout({ isRecording: recordingParent });

  // ---------------------------------------------------------------------------
  // Visibility tracking — IntersectionObserver-based section visibility events
  // ---------------------------------------------------------------------------
  const transcriptionPanelRef = useVisibilityTracking('transcription-panel');
  const aiNotesPanelRef = useVisibilityTracking('ai-notes-panel');
  const manualNotesPanelRef = useVisibilityTracking('manual-notes-panel');
  const patientDetailsRef = useVisibilityTracking('patient-details-header');

  // Derive combined connection state for UI (buttons, banner, etc.)
  const connectionState = useMemo(() => {
    // Network offline takes precedence - can't do anything without internet
    if (!isOnline) {
      return recordingParent ? 'reconnecting' : 'disconnected';
    }
    // uncomment Below return , while working in local as ws connection wont be established in local
    // return 'connected';

    // Network online - reflect WebSocket state
    switch (wsState) {
      case 'connected':
        return 'connected';
      case 'connecting':
      case 'reconnecting':
        return 'reconnecting';
      case 'disconnected':
      case 'idle':
      default:
        return 'disconnected';
    }
  }, [isOnline, wsState, recordingParent]);

  const selectedSession = useMemo(() => {
    const effectiveId = String(selectedSummarySessionId || sessionId || "");
    return sessions.find((s) => String(s.id) === effectiveId);
  }, [sessions, selectedSummarySessionId, sessionId]);

  const selectedSessionType =
    selectedSession?.session_type ||
    selectedSession?.type ||
    selectedSession?.category ||
    patientDetails?.sessionType;

  const effectiveSessionType =
    currentSessionType ??
    selectedSessionType ??
    location.state?.session_type ??
    patientDetails?.sessionType ??
    "—";

  const canViewPastSummary =
    !pastSummaryDirty && pastSummaryStatus === "success" && !!pastSummary;

  const getTranscriptDetails = async function () {
    if (id && session_id) {
      try {
        // First, try to get same-day recording transcript
        const recordingTranscriptDetails = await getRecordingTranscript(id, session_id);

        if (recordingTranscriptDetails.status === 204) {
          // No same-day transcript found - fallback to getTranscript for previous recordings
          console.log('[Transcript] No same-day recording found, checking for previous recordings...');

          try {
            const previousTranscriptDetails = await getTranscript(id, session_id);

            if (previousTranscriptDetails?.data?.content) {
              const decoded = decodeBase64(previousTranscriptDetails.data.content);
              setFetchedTranscript(decoded);
              setTranscriptReceived(true);
              console.log('[Transcript] Loaded previous recording transcript');
              return;
            }
          } catch (prevErr) {
            console.log('[Transcript] No previous recording found:', prevErr?.response?.status);
          }

          // FIX (Issue #2): Always set empty state if no transcript found
          setFetchedTranscript("");
          setTranscriptReceived(false);
          console.log('[Transcript] No transcript available');
          return;
        }

        // Same-day recording transcript found
        const decoded = decodeBase64(recordingTranscriptDetails.data.content);
        setFetchedTranscript(decoded);
        setTranscriptReceived(true);
        console.log('[Transcript] Loaded same-day recording transcript');
      } catch (err) {
        console.error('[Transcript] Error loading transcript:', err);
        
        // Check for UNSUPPORTED_SESSION_TYPE error from recording-transcript endpoint
        if (err?.response?.data?.error_code === 'UNSUPPORTED_SESSION_TYPE') {
          console.warn('[Transcript] Unsupported session type detected from recording-transcript API');
          setIsValidAppointment(false);
          setFetchedTranscript("");
          setTranscriptReceived(false);
          return;
        }
        
        // FIX (Issue #2): Set empty state on error
        setFetchedTranscript("");
        setTranscriptReceived(false);
        
        notification.error({
          message: "The transcription could not be loaded.",
          description: err?.response?.data?.reason || "Please try after some time.",
        });
      }
    }
  };

  const getPrevSessions = async () => {
    try {
      setSessionsLoading(true);
      const [start, end] = dateRange || [];
      const params =
        start && end
          ? {
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
          }
          : undefined;

      const sessionsData = await getSessions(id, params);

      setCurrentSessionType(
        sessionsData?.data?.find((x) => x?.session_id == session_id)
          ?.session_type
      );

      const sessionDataModified =
        sessionsData?.data
          ?.filter((x) => x?.session_id !== Number(session_id))
          ?.map((x, i) => ({
            ...x,
            id: x?.session_id,
            title: `Session #${i + 1}`,
            date: formatReadableDate(x?.session_date),
            time: extractTimeWithoutSeconds(x?.session_date),
            patientId: id,
            category: "Therapists",
          })) || [];

      setSessions(sessionDataModified);

      const allIds = sessionDataModified.map((s) => String(s.id));
      setSelectedSessionIds(allIds);
    } catch (e) {
      console.error("Error loading sessions:", e);
      message.error("No past sessions found");
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (session_id) {
      setNoteCommentSessionId(session_id);
    }
  }, [session_id]);

  useEffect(() => {
    if (id) getPrevSessions();
  }, [id, selectedDoctorId]);

  useEffect(() => {
    const dateRangeReset =
      dateRange === null || (dateRange[0] === null && dateRange[1] === null);
    if (dateRangeReset) {
      getPrevSessions();
    }
  }, [dateRange]);

  useEffect(() => {
    getTranscriptDetails();
  }, [id, session_id]);

  // -----------------------------------------------------------------------
  // FIX (RCA 5B): Reset all session-specific state when patient or session changes.
  // Prevents stale recording data, transcripts, notes, and UI flags from a
  // previous session bleeding into the next one.
  // -----------------------------------------------------------------------
  useEffect(() => {
    setHasConsent(false);

    // Stop any active recording before switching context
    if (recordingParent) {
      console.warn('[Session] Patient/session changed while recording — dispatching recording:stop');
      window.dispatchEvent(new CustomEvent('recording:stop'));
    }

    // Reset recording-related state
    setRecordingParent(false);
    setRecordingTranscript([]);
    setLivePreview(new Map());
    setTimer("00:00:00");

    // Reset note generation state
    setNoteJSON({});
    setFieldMappings({});
    setLoadingMappings(false);
    setGeneratingNotes(false);
    setPollingNote(false);
    setFetchingNote(false);
    setNoteSubmitted(false);
    setNoteLocked(false);
    setIsNoteSigned(false);
    setIsEhrSubmitted(false);

    // Reset notes text
    setNotes("");

    // Reset WebSocket / connection state
    setWsState('idle');
    setReconnectAttempt(0);
    setWsConnectionFailed(false);
  }, [id, sessionId, selectedDoctorId]);

  // ---------------------------------------------------------------------------
  // Listen for WebSocket connection failure events from ControlButtons (reconnect exhausted)
  // This keeps the banner visible even after recordingParent is set to false
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleWsConnectionFailed = () => {
      console.warn('[Session] WebSocket connection permanently failed during recording');
      setWsConnectionFailed(true);
    };

    window.addEventListener('recording:wsConnectionFailed', handleWsConnectionFailed);
    return () => {
      window.removeEventListener('recording:wsConnectionFailed', handleWsConnectionFailed);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Listen for UNSUPPORTED_SESSION_TYPE events from child components
  // (secondary detection for direct URL access / page refresh)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleUnsupportedSessionType = () => {
      console.warn('[Session] Unsupported session type detected — marking appointment as invalid');
      setIsValidAppointment(false);
    };

    window.addEventListener('session:unsupported-type', handleUnsupportedSessionType);
    return () => {
      window.removeEventListener('session:unsupported-type', handleUnsupportedSessionType);
    };
  }, []);

  // Clear wsConnectionFailed when user starts a new recording
  useEffect(() => {
    if (recordingParent) {
      setWsConnectionFailed(false);
    }
  }, [recordingParent]);

  useEffect(() => {
    const fetchCore = async () => {
      if (!id || !sessionId) return;
      setLoadingCore(true);
      try {
        // Only fetch patient details - transcript is handled by getTranscriptDetails
        const patientResult = await getPatientDetails(id, sessionId);

        const modifiedPatientData = patientResult?.data;
        const patient = {
          name: modifiedPatientData?.name,
          age: modifiedPatientData?.age,
          gender: modifiedPatientData?.gender,
          contactNumber: modifiedPatientData?.phone_number,
          sessionType: "Prescription",
        };
        setPatientDetails(patient);

        setPastSummary(null);
        setPastSummaryStatus(null);
        setPastSummaryErrorMessage(null);
        setPastSummarySessionsProcessed(null);
      } catch (err) {
        console.error("Error loading patient data:", err);
        message.error("Unexpected error loading session data");
      } finally {
        setLoadingCore(false);
      }
    };

    fetchCore();
  }, [id, sessionId, selectedDoctorId]);

  useEffect(() => {
    if (transcriptReceived) setActiveTab("2");
  }, [transcriptReceived]);

  const fetchPastSummary = async () => {
    if (!id || selectedSessionIds.length === 0) {
      setPastSummary(null);
      setPastSummaryStatus(null);
      setPastSummaryErrorMessage(null);
      setPastSummarySessionsProcessed(null);
      setLoadingPastSummary(false);
      setPastSummaryDirty(true);
      return;
    }

    try {
      setLoadingPastSummary(true);

      const pastSummaryData = await getPastSummary({
        patientId: Number(id),
        sessionIds: selectedSessionIds.map(Number),
        excludeSessionId: Number(session_id),
      });

      const status = pastSummaryData?.data?.status;
      const content = pastSummaryData?.data?.content ?? null;
      const errorMessage = pastSummaryData?.data?.error_message ?? null;
      const sessionsProcessed = pastSummaryData?.data?.sessions_processed;

      setPastSummaryStatus(status || null);
      setPastSummaryErrorMessage(errorMessage);
      setPastSummarySessionsProcessed(
        typeof sessionsProcessed === "number" ? sessionsProcessed : null
      );

      if (status === "success" && content) {
        setPastSummary(content);
        setPastSummaryDirty(false);
        if (typeof sessionsProcessed === "number") {
          message.success(
            `Past summary generated from ${sessionsProcessed} past session${
            sessionsProcessed === 1 ? "" : "s"
            }`
          );
        }
        return;
      }

      setPastSummary(null);

      if (status === "no_data") {
        message.info(errorMessage || "No EHR notes found for the selected sessions");
        setPastSummaryDirty(false);
        return;
      }

      if (status === "parsing_failed") {
        message.error(errorMessage || "Past summary parsing failed");
        setPastSummaryDirty(true);
        return;
      }

      if (status === "error") {
        message.error(errorMessage || "Failed to load past summary");
        setPastSummaryDirty(true);
        return;
      }

      message.error("Failed to load past summary");
      setPastSummaryDirty(true);
    } catch (error) {
      console.error("Error fetching past summary:", error);
      setPastSummary(null);
      setPastSummaryStatus("error");
      setPastSummaryErrorMessage("Failed to load past summary");
      setPastSummarySessionsProcessed(null);
      message.error("Failed to load past summary");
      setPastSummaryDirty(true);
    } finally {
      setLoadingPastSummary(false);
    }
  };

  useEffect(() => {
    setPastSummary(null);
    setPastSummaryStatus(null);
    setPastSummaryErrorMessage(null);
    setPastSummarySessionsProcessed(null);
    setPastSummaryDirty(true);
  }, [id, selectedDoctorId]);

  useEffect(() => {
    setPastSummaryDirty(true);
  }, [selectedSessionIds]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleSearch = () => setSearchVisible(true);
  const handleCloseSearch = () => {
    setSearchVisible(false);
    setSearchQuery("");
  };

  // COMMENTED OUT (RCA UX5): handleSubmitFeedback used the mock submitFeedback function.
  // Real feedback is now handled in AINotesView.js via axiosInstance.post to /notes/ai-feedback.
  // The old FeedbackModal component below still references this — it can be removed in a future cleanup.
  const handleSubmitFeedback = async () => {
    // try {
    //   await submitFeedback(feedback);
    //   message.success("Feedback submitted successfully");
    //   setFeedbackVisible(false);
    //   setFeedback("");
    // } catch (error) {
    //   message.error("Failed to submit feedback");
    // }
    console.warn('[Session] handleSubmitFeedback called but submitFeedback mock has been removed. Real feedback is in AINotesView.');
    message.info("Please use the feedback buttons on the AI Notes tab.");
    setFeedbackVisible(false);
  };

  const handleViewPostSummary = async () => {
    setPostSummaryVisible(true);
  };

  // Navigation blocker integration
  const [uploading, setUploading] = useState(false);

  const {
    showConfirmation,
    blockingReason,
    handleConfirmLeave,
    handleCancelLeave,
  } = useNavigationBlocker({
    recordingParent,
    notes,
    generatingNotes,
    uploading,
    pollingNote,
    fetchingNote,
    isNoteSigned,
    noteLocked,
    onBeforeLeave: async () => {
      // Auto-save manual notes before leaving
      if (notes && id && noteCommentSessionId) {
        try {
          const { saveNoteCommentAPI } = await import('../services/api');
          await saveNoteCommentAPI(id, noteCommentSessionId, notes);
          message.success('Notes saved successfully');
        } catch (error) {
          console.error('Error saving notes:', error);
          message.error('Failed to save notes');
        }
      }

      // Stop recording gracefully if active
      if (recordingParent) {
        window.dispatchEvent(new CustomEvent('recording:stop'));
      }
    },
  });

  return (
    <Layout className="h-screen">
      <Header
        onLogout={() => instance.logout()}
        selectedDoctorId={selectedDoctorId}
        onSelectDoctor={setSelectedDoctorId}
        options={options}
      />

      <div className="flex-1 flex flex-col">
        <div className="flex justify-between items-center px-7 py-5">
          <div className="flex gap-6 items-center">
            <Link
              to="/"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <div className="flex items-center gap-2 text-accent">
                <LeftOutlined /> BACK
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-4"></div>

          <Button
            onClick={() => setSettingsVisible(true)}
            icon={<SettingOutlined />}
          />
        </div>

        {/* Connection Status Banner — always visible during recording or when connection is not healthy */}
        {(connectionState !== 'connected' || recordingParent || wsConnectionFailed) && (
          <div className="px-7">
            <ConnectionStatus
              connectionState={connectionState}
              reconnectAttempt={reconnectAttempt}
              maxAttempts={5}
              isRecording={recordingParent || wsConnectionFailed}
              isOnline={isOnline}
              wsState={wsState}
              showWhenConnected={recordingParent}
              wsConnectionFailed={wsConnectionFailed}
            />
          </div>
        )}

        {/* Invalid Appointment Banner */}
        {!isValidAppointment && (
          <div className="px-7 mb-2">
            <Alert
              type="warning"
              showIcon
              message="Unsupported Appointment Type"
              description="This appointment type doesn't have a supported template. Please select the correct appointment type for this session in AMD."
              banner
            />
          </div>
        )}

        <div className="flex gap-4 px-7 pb-7 flex-grow">
          <SessionSidebar
            pastSummary={pastSummary}
            onGeneratePastSummary={() => {
              initialSummaryFetchedRef.current = true;
              fetchPastSummary();
            }}
            canViewPastSummary={canViewPastSummary}
            sessions={sessions}
            onPrimaryPastSummaryAction={() => {
              if (!canViewPastSummary) {
                fetchPastSummary();
              } else {
                handleViewPostSummary();
              }
            }}
            currentSessionId={sessionId || ""}
            selectedSessionIds={selectedSessionIds}
            onChangeSelectedSessions={setSelectedSessionIds}
            onViewPostSummary={handleViewPostSummary}
            loadingPastSummary={loadingPastSummary}
            sessionsLoading={sessionsLoading}
            dateRange={dateRange}
            onSearch={getPrevSessions}
            onChangeDateRange={setDateRange}
          />

          <div className="flex-1 flex flex-col">
            <div className="min-h-[72px] flex gap-4 mb-4">
              <div className="flex-1" ref={patientDetailsRef}>
                {loadingCore ? (
                  <div className="bg-white rounded-lg p-4 flex items-center flex-col justify-center min-h-[134px]">
                    <Spin tip="Loading patient details..." size="small" />
                    <p className="mt-4 text-gray-500 text-sm font-medium">
                      Loading patient details...
                    </p>
                  </div>
                ) : (
                  <PatientDetailsHeader
                    patientDetails={patientDetails}
                    sessionType={effectiveSessionType}
                  />
                )}
              </div>

              <SessionRightPanel
                transcriptReceived={transcriptReceived}
                timer={timer}
                isNoteSigned={isNoteSigned}
                setIsNoteSigned={setIsNoteSigned}
                setTimer={setTimer}
                setTemplate={setTemplate}
                meetingStatus={meetingStatus}
                recordingParent={recordingParent}
                setRecordingParent={setRecordingParent}
                notes={notes}
                parentRecording={parentRecording}
                setFetchingNote={setFetchingNote}
                setPollingNote={setPollingNote}
                onNoteLocked={() => setNoteLocked(true)}
                onNotesChange={setNotes}
                setNoteJSON={setNoteJSON}
                setFetchedTranscript={setFetchedTranscript}
                setTranscriptReceived={setTranscriptReceived}
                setFieldMappings={setFieldMappings}
                setLoadingMappings={setLoadingMappings}
                template={template}
                session_id={sessionId}
                noteCommentSession_id={noteCommentSessionId}
                patient_id={id}
                setRecordingTranscript={setRecordingTranscript}
                setLivePreview={setLivePreview}
                hasConsent={hasConsent}
                onConsentGiven={handleConsentGiven}
                onConsentClear={handleConsentClear}
                setUploading={setUploading}
                connectionState={connectionState}
                wsState={wsState}
                setWsState={setWsState}
                isOnline={isOnline}
                isEhrSubmitted={isEhrSubmitted}
                setIsEhrSubmitted={setIsEhrSubmitted}
                reconnectAttempt={reconnectAttempt}
                setReconnectAttempt={setReconnectAttempt}
                isValidAppointment={isValidAppointment}
                generatingNotes={generatingNotes}
                pollingNote={pollingNote}
              />
            </div>

            <div
              className="bg-white rounded-xl border border-gray-100 flex-grow flex flex-col shadow-sm"
              style={{
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                className="flex-grow flex flex-col"
                style={{ padding: "0" }}
                tabBarStyle={{
                  margin: "0",
                  borderBottom: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  borderRadius: "12px 12px 0 0",
                }}
                size="large"
              >
                <TabPane
                  metrics-label="View Transcription"
                  tab={
                    <div className="flex items-center gap-2 px-1 py-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={
                          activeTab === "2" ? "text-blue-600" : "text-gray-500"
                        }
                      >
                        <path
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        metrics-label="View Transcription"
                        className={`font-semibold text-sm tracking-wide ${
                        activeTab === "2" ? "text-blue-600" : "text-gray-600"
                          }`}
                      >
                        TRANSCRIPTION
                      </span>
                    </div>
                  }
                  key="2"
                >
                  <div ref={transcriptionPanelRef} className="flex h-full" style={{ minHeight: "400px" }}>
                    <div className="flex-1 p-5">
                      {process.env.REACT_APP_DEV_LOGIN === "true" && (
                        <div className="mb-3 flex justify-end">
                          <Button
                            size="small"
                            icon={<UploadOutlined />}
                            onClick={() => setUploadOpen(true)}
                          >
                            Upload transcript (demo)
                          </Button>
                        </div>
                      )}
                      {loadingCore ? (
                        <div
                          className="flex flex-col items-center justify-center h-full"
                          style={{ minHeight: "300px" }}
                        >
                          <Spin size="large" />
                          <p className="mt-4 text-gray-500 text-sm font-medium">
                            Loading transcription...
                          </p>
                        </div>
                      ) : recordingParent ? (
                        <TranscriptRecording
                          recordingTranscript={recordingTranscript}
                          livePreview={livePreview}
                        />
                      ) : fetchedTranscript ? (
                        <TranscriptView
                          transcript={fetchedTranscript}
                          transcriptReceived={transcriptReceived}
                        />
                      ) : (
                        <TranscriptRecording
                          recordingTranscript={recordingTranscript}
                          livePreview={livePreview}
                        />
                      )}
                    </div>
                  </div>
                </TabPane>

                <TabPane
                  tab={
                    <div className="flex items-center gap-2 px-1 py-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={
                          activeTab === "3" ? "text-blue-600" : "text-gray-500"
                        }
                      >
                        <path
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        className={`font-semibold text-sm tracking-wide ${
                        activeTab === "3" ? "text-blue-600" : "text-gray-600"
                          }`}
                      >
                        AI NOTES
                      </span>
                    </div>
                  }
                  key="3"
                  metrics-label="View AI notes"
                >
                  <div ref={aiNotesPanelRef} className="flex h-full" style={{ minHeight: "400px" }}>
                    <div className="flex-1 p-5">
                      {loadingCore ? (
                        <div
                          className="flex flex-col items-center justify-center h-full"
                          style={{ minHeight: "300px" }}
                        >
                          <Spin size="large" />
                          <p className="mt-4 text-gray-500 text-sm font-medium">
                            Loading AI notes...
                          </p>
                        </div>
                      ) : noteJSON ? (
                        <ErrorBoundary
                          resetKeys={[noteJSON]}
                          fallback={
                            <div
                              className="flex items-center justify-center h-full text-gray-500 font-medium"
                              style={{ minHeight: "300px" }}
                            >
                              This note couldn't be displayed. Please refresh the
                              page to try again.
                            </div>
                          }
                        >
                          <AINotesView
                            isNoteSigned={isNoteSigned}
                            noteJSON={noteJSON}
                            template={template}
                            setNoteSubmitted={setNoteSubmitted}
                            setFeedbackVisible={setFeedbackVisible}
                            loadingMappings={loadingMappings}
                            fieldMappings={fieldMappings}
                            session_id={sessionId}
                            patient_id={id}
                            clinician_name={location.state?.clinician_name}
                            session_date={location.state?.session_date}
                            fetchingNote={fetchingNote}
                            pollingNote={pollingNote}
                            recordingParent={recordingParent}
                            setRecordingParent={setRecordingParent}
                            isNoteLocked={noteLocked}
                            connectionState={connectionState}
                            isOnline={isOnline}
                            isEhrSubmitted={isEhrSubmitted}
                            isValidAppointment={isValidAppointment}
                          />
                        </ErrorBoundary>
                      ) : (
                        <div
                          className="flex items-center justify-center h-full text-gray-500 font-medium"
                          style={{ minHeight: "300px" }}
                        >
                          If no note appears after the generation, try
                          refreshing the page.
                        </div>
                      )}
                    </div>
                  </div>
                </TabPane>
              </Tabs>

              <div
                ref={manualNotesPanelRef}
                className="border-l border-gray-200"
                style={{
                  width: "380px",
                  minWidth: "380px",
                  maxWidth: "380px",
                  flexShrink: 0,
                }}
              >
                <ManualNotes
                  hasRecordingStarted={recordingParent}
                  key={`${id}-${noteCommentSessionId}`}
                  generateAINotesClicked={() => handleTabChange("3")}
                  isNoteSigned={isNoteSigned}
                  noteCommentSession_id={noteCommentSessionId}
                  patient_id={id}
                  onNotesChange={setNotes}
                  notes={notes}
                  noteSubmitted={noteSubmitted}
                  setNoteJSON={setNoteJSON}
                  setPollingNote={setPollingNote}
                  setGeneratingNotes={setGeneratingNotes}
                  generatingNotes={generatingNotes}
                  pollingNote={pollingNote}
                  setActiveTab={setActiveTab}
                  onLockStateChange={setNoteLocked}
                  connectionState={connectionState}
                  isOnline={isOnline}
                  isValidAppointment={isValidAppointment}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SearchModal
        visible={searchVisible}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onClose={handleCloseSearch}
      />

      <FeedbackModal
        visible={feedbackVisible}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        onSubmit={handleSubmitFeedback}
        onClose={() => setFeedbackVisible(false)}
      />

      <SettingsModal
        selectedDoctorId={selectedDoctorId}
        visible={settingsVisible}
        onClose={() => setSettingsVisible(false)}
      />

      <TranscriptUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        patientId={id}
        sessionId={session_id}
        onUploaded={(text) => {
          setFetchedTranscript(text);
          setTranscriptReceived(true);
        }}
      />

      <Modal
        open={postSummaryVisible}
        onCancel={() => setPostSummaryVisible(false)}
        title={`Past Summary (${selectedSessionIds.length} session${
        selectedSessionIds.length === 1 ? "" : "s"
          })${
          typeof pastSummarySessionsProcessed === "number" &&
            pastSummaryStatus === "success"
            ? ` • Generated from ${pastSummarySessionsProcessed} past session${
            pastSummarySessionsProcessed === 1 ? "" : "s"
            }`
            : ""
          }`}
        width={960}
        footer={null}
        destroyOnClose
      >
        {loadingPastSummary ? (
          <div className="flex items-center justify-center h-64">
            <Spin tip="Loading summary..." />
            <p className="mt-4 text-gray-500 text-sm font-medium">
              Loading summary...
            </p>
          </div>
        ) : pastSummaryStatus === "success" && pastSummary ? (
          <div className="max-h-[70vh] overflow-y-auto pt-2">
            <PastSummaryView pastSummary={pastSummary} />
          </div>
        ) : pastSummaryStatus === "no_data" ? (
          <div className="flex items-center justify-center h-40 text-gray-600 font-medium">
            {pastSummaryErrorMessage || "No EHR notes found for the selected sessions"}
          </div>
        ) : (
          <div className="flex items-center justify-center h-40 text-gray-600 font-medium">
            {pastSummaryErrorMessage || "Past summary is unavailable"}
          </div>
        )}
      </Modal>

      {/* Navigation Confirmation Modal */}
      <NavigationConfirmModal
        visible={showConfirmation}
        blockingReason={blockingReason}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </Layout>
  );
};

export default SessionDetails;
